import React, { createContext, useContext, useEffect, useState } from 'react';

const FontReadyContext = createContext(false);

const SF_PRO_SPEC = '16px "SF Pro Display"';
const POLL_INTERVAL_MS = 50;
const MAX_WAIT_MS = 1500;

export function FontReadyProvider({ children }) {
  const [fontReady, setFontReady] = useState(false);

  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts) {
      setFontReady(true);
      return;
    }

    // Kick off font load as early as possible (works with preload)
    try {
      document.fonts.load(SF_PRO_SPEC, '400');
    } catch (_) {}

    let cancelled = false;

    const check = () => document.fonts.check(SF_PRO_SPEC, '400');

    const setReady = () => {
      if (cancelled) return;
      setFontReady(true);
    };

    const waitForFont = () => {
      if (cancelled) return;
      if (check()) {
        setReady();
        return;
      }
      const deadline = Date.now() + MAX_WAIT_MS;
      const id = setInterval(() => {
        if (cancelled || Date.now() > deadline) {
          clearInterval(id);
          if (!cancelled) setFontReady(true);
          return;
        }
        if (check()) {
          clearInterval(id);
          setReady();
        }
      }, POLL_INTERVAL_MS);
      return () => clearInterval(id);
    };

    // Poll immediately (e.g. font cached) instead of waiting for document.fonts.ready
    const cleanup = waitForFont();
    return () => {
      cancelled = true;
      if (typeof cleanup === 'function') cleanup();
    };
  }, []);

  return (
    <FontReadyContext.Provider value={fontReady}>
      {children}
    </FontReadyContext.Provider>
  );
}

export function useFontReady() {
  return useContext(FontReadyContext);
}
